import { useState, useEffect } from 'react'
import { useAuth } from '@/modules/auth'
import { BetaInvitationsApi } from '@/shared/api/betaInvitations'
import { PageLayout } from '@/ui/layout'
import { Button } from '@/ui/components'
import { Input } from '@/ui/components'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/ui/components'
import { Badge } from '@/ui/components'
import { Mail, CheckCircle, Clock, XCircle, Loader2, AlertCircle, Send, Info } from '@/ui/icons'
import type { BetaInvitation } from '@/shared/api/betaInvitations'

// Helper function to get user's display name
function getUserDisplayName(user: any): string {
  if (!user) return ''
  
  const metadata = user.user_metadata ?? {}
  const metadataName =
    (typeof metadata.full_name === 'string' && metadata.full_name) ||
    (typeof metadata.name === 'string' && metadata.name) ||
    (typeof metadata.display_name === 'string' && metadata.display_name) ||
    null

  if (metadataName) return metadataName
  
  // Fallback to email username
  if (user.email) {
    return user.email.split('@')[0]
      .split(/[._-]/)
      .map((part: string) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
      .join(' ')
  }
  
  return ''
}

export default function BetaInvitations() {
  const { user, isBetaUser, getAccessToken } = useAuth()
  const [email, setEmail] = useState('')
  const [invitations, setInvitations] = useState<BetaInvitation[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [isCreating, setIsCreating] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)
  const [resendingId, setResendingId] = useState<string | null>(null)

  const userName = getUserDisplayName(user)

  useEffect(() => {
    if (!isBetaUser) {
      return
    }

    const loadInvitations = async () => {
      try {
        const token = await getAccessToken()
        if (!token) return

        const api = new BetaInvitationsApi(getAccessToken)
        const response = await api.getInvitations()
        setInvitations(response.data)
      } catch (err: any) {
        setError(err?.message || 'Failed to load invitations')
      } finally {
        setIsLoading(false)
      }
    }

    loadInvitations()
  }, [isBetaUser, getAccessToken])

  const handleCreateInvitation = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setSuccess(null)

    if (!email.trim()) {
      setError('Please enter an email address')
      return
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(email)) {
      setError('Please enter a valid email address')
      return
    }

    setIsCreating(true)

    try {
      const api = new BetaInvitationsApi(getAccessToken)
      const response = await api.createInvitation({ email: email.trim() })
      setInvitations([response.data, ...invitations])
      setEmail('')
      setSuccess(`Invitation sent to ${response.data.email}`)
    } catch (err: any) {
      const errorMessage = err?.message || err?.response?.data?.error || 'Failed to create invitation'
      setError(errorMessage)
    } finally {
      setIsCreating(false)
    }
  }

  const handleResendInvitation = async (invitation: BetaInvitation) => {
    setError(null)
    setSuccess(null)
    setResendingId(invitation.id)

    try {
      const api = new BetaInvitationsApi(getAccessToken)
      await api.resendInvitation(invitation.id)
      setSuccess(`Invitation email resent to ${invitation.email}`)
    } catch (err: any) {
      const errorMessage = err?.message || err?.response?.data?.error || 'Failed to resend invitation'
      setError(errorMessage)
    } finally {
      setResendingId(null)
    }
  }

  const getStatusBadge = (invitation: BetaInvitation) => {
    switch (invitation.status) {
      case 'accepted':
        return <Badge variant="default" className="bg-success text-success-foreground"><CheckCircle className="w-3 h-3 mr-1" />Accepted</Badge>
      case 'expired':
        return <Badge variant="destructive"><XCircle className="w-3 h-3 mr-1" />Expired</Badge>
      case 'pending':
        return <Badge variant="outline"><Clock className="w-3 h-3 mr-1" />Pending</Badge>
      default:
        return <Badge variant="outline">{invitation.status}</Badge>
    }
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  if (!isBetaUser) {
    return (
      <PageLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Card className="max-w-md">
            <CardHeader>
              <CardTitle>Access Denied</CardTitle>
              <CardDescription>
                You must be a beta user to invite others.
              </CardDescription>
            </CardHeader>
          </Card>
        </div>
      </PageLayout>
    )
  }

  return (
    <PageLayout>
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-foreground mb-2">
            {userName ? `Hi ${userName}!` : 'Beta User Invitations'}
          </h1>
          <p className="text-muted-foreground">
            {userName 
              ? `Invite others to join SitRepp.io as beta users. They'll receive an email with a signup link.`
              : `Invite others to join SitRepp.io as beta users. They'll receive an email with a signup link.`
            }
          </p>
        </div>

        {/* Information Card */}
        <Card className="mb-8 border-adventure-200 bg-adventure-50/50">
          <CardContent className="pt-6">
            <div className="flex gap-3">
              <Info className="w-5 h-5 text-adventure-600 mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <h3 className="font-semibold text-foreground mb-2">What Beta Invitations Do</h3>
                <div className="text-sm text-muted-foreground space-y-2">
                  <p>
                    {userName 
                      ? `When you send a beta invitation, ${userName}, the recipient will receive an email that grants them access to create an account and use SitRepp.io as a beta user.`
                      : `When you send a beta invitation, the recipient will receive an email that grants them access to create an account and use SitRepp.io as a beta user.`
                    }
                  </p>
                  <p className="font-medium text-foreground">
                    Important: Beta invitations give platform access only. To join specific trips, you'll need to send a trip invitation to the person you want to invite.
                  </p>
                  <p className="text-muted-foreground">
                    Note: You can still invite guests to trips without them needing a beta account. Trip invitations work independently and guests can join trips without creating an account.
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Create Invitation Form */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle>Invite Beta User</CardTitle>
            <CardDescription>
              Enter an email address to send a beta invitation
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleCreateInvitation} className="space-y-4">
              <div>
                <label htmlFor="invite-email" className="block text-sm font-medium text-foreground mb-2">
                  Email Address
                </label>
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                    <Input
                      id="invite-email"
                      type="email"
                      placeholder="friend@example.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="pl-10"
                      disabled={isCreating}
                      required
                    />
                  </div>
                  <Button
                    type="submit"
                    disabled={isCreating || !email.trim()}
                  >
                    {isCreating ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <Mail className="w-4 h-4 mr-2" />
                        Send Invitation
                      </>
                    )}
                  </Button>
                </div>
              </div>

              {error && (
                <div className="bg-error/10 border border-error/20 text-error px-4 py-3 rounded flex items-start">
                  <AlertCircle className="w-5 h-5 mr-2 mt-0.5 flex-shrink-0" />
                  <span className="text-sm">{error}</span>
                </div>
              )}

              {success && (
                <div className="bg-success/10 border border-success/20 text-success px-4 py-3 rounded flex items-start">
                  <CheckCircle className="w-5 h-5 mr-2 mt-0.5 flex-shrink-0" />
                  <span className="text-sm">{success}</span>
                </div>
              )}
            </form>
          </CardContent>
        </Card>

        {/* Invitations List */}
        <Card>
          <CardHeader>
            <CardTitle>Your Invitations</CardTitle>
            <CardDescription>
              {invitations.length === 0 
                ? 'No invitations sent yet'
                : `${invitations.length} invitation${invitations.length !== 1 ? 's' : ''} sent`
              }
            </CardDescription>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
              </div>
            ) : invitations.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <Mail className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>You haven't sent any invitations yet.</p>
              </div>
            ) : (
              <div className="space-y-4">
                {invitations.map((invitation) => (
                  <div
                    key={invitation.id}
                    className="border border-border rounded-lg p-4 hover:bg-accent/50 transition-colors"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <span className="font-medium text-foreground">{invitation.email}</span>
                          {getStatusBadge(invitation)}
                        </div>
                        <div className="text-sm text-muted-foreground space-y-1">
                          <p>Sent: {formatDate(invitation.created_at)}</p>
                          <p>Expires: {formatDate(invitation.expires_at)}</p>
                          {invitation.accepted_at && (
                            <p className="text-success">Accepted: {formatDate(invitation.accepted_at)}</p>
                          )}
                        </div>
                      </div>
                      {invitation.status === 'pending' && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleResendInvitation(invitation)}
                          disabled={resendingId === invitation.id}
                          className="ml-4"
                        >
                          {resendingId === invitation.id ? (
                            <>
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                              Resending...
                            </>
                          ) : (
                            <>
                              <Send className="w-4 h-4 mr-2" />
                              Resend Email
                            </>
                          )}
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </PageLayout>
  )
}

